import type { Dispatch } from "@reduxjs/toolkit";
import { t } from "i18next";
import { enqueueNotification } from "../slice/notification/notificationSlice";

/**
 * One place that decides what a failed request says to the customer (#1970).
 *
 * Every booking-creation and payment-initialisation flow used to end its
 * failure in `Logger` + `ErrorTracker` and nowhere else: the button simply did
 * nothing. `createVenueReservationFlow` got the first fix (#1964) with the
 * mapping inlined; repeating that mapping in six more flows is exactly the
 * drift this repo has been bitten by (#1875 -> #1890), so it lives here and
 * every flow imports it.
 *
 * Diagnostics are unaffected — this adds a user-facing message, it does not
 * replace `Logger`/`ErrorTracker`.
 */

/** i18n keys behind the status mapping, so tests can assert on them. */
export const BOOKING_FAILURE_MESSAGE_KEYS = {
  /** 403 — the thing exists, but not for this customer any more. */
  forbidden: "book_location.error.no_longer_available_to_you",
  /** 409 — taken in the meantime; nobody can have it. */
  conflict: "book_location.error.no_longer_available",
  /** The start date is inside the tenant's lead-time buffer. */
  bookingWindowLead: "book_location.error.booking_window_lead",
  /** The start date is beyond the tenant's booking horizon. */
  bookingWindowHorizon: "book_location.error.booking_window_horizon",
  bookingWindowMinStay: "book_location.error.booking_window_min_stay",
  /** The screen showed "covered", the server could not confirm it. */
  coverageNotConfirmed: "book_location.error.coverage_not_confirmed",
  /** Anything else: nothing the customer can act on beyond retrying. */
  generic: "book_location.error.booking_failed",
} as const;

/**
 * The booking window refuses with a 400 and a machine-readable `msg`
 * (`backend/src/core/lib/bookingWindowConfig.ts`), which is what makes it
 * different from every other 400: the customer must change the DATE, and no
 * amount of retrying will help.
 *
 * QA #2764 D1 caught the consequence — the toast said "Bitte versuche es
 * erneut" for a refusal that can never succeed on retry. Same class as
 * #2255/#2258.
 *
 * The code is read, not the accompanying `message`: the server composes that
 * sentence in English only, and `fallbackLng` is `de`, so passing it through
 * would show German customers English. The trade is that the localized
 * sentence cannot name the exact number of days — the server embeds it in the
 * English prose rather than sending it as a field. Naming it would need a
 * `leadDays` / `horizonMonths` value on the refusal body; worth asking for,
 * not worth parsing out of a sentence.
 */
// `string | undefined` deliberately: with a bare `Record<string, string>` the
// compiler types every lookup as a `string`, so the `undefined` checks below
// read as dead code to a type-aware linter while the runtime value is absent
// for any code we do not map.
const BOOKING_WINDOW_KEYS: Record<string, string | undefined> = {
  booking_window_lead: BOOKING_FAILURE_MESSAGE_KEYS.bookingWindowLead,
  booking_window_horizon: BOOKING_FAILURE_MESSAGE_KEYS.bookingWindowHorizon,
  booking_window_min_stay: BOOKING_FAILURE_MESSAGE_KEYS.bookingWindowMinStay,
  // Not a booking-window code, but the same kind of refusal: retrying the same
  // submit cannot help — the customer has to start over and see the real price.
  coverage_not_confirmed: BOOKING_FAILURE_MESSAGE_KEYS.coverageNotConfirmed,
};

/**
 * The refusal code of a failed request, if it carries one.
 *
 * `axiosBaseQuery` normalises every API failure to `{ status, data }`, so the
 * body is `error.data` — the same shape `httpStatusOf` reads `status` from.
 *
 * The code arrives in one of two envelopes. `orders/from-reservation` puts it
 * at the top (`{ msg, message }`); `POST /reservation` answers through the old
 * backend's `processError`, which nests it (`{ message, details: { msg } }`).
 * Reading only the top level showed every paid room booking's window refusal
 * as "Bitte versuche es erneut".
 */
export const bookingRefusalCodeOf = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return undefined;
  const msg = (data as { msg?: unknown }).msg;
  if (typeof msg === "string") return msg;
  const details = (data as { details?: unknown }).details;
  if (!details || typeof details !== "object") return undefined;
  const nested = (details as { msg?: unknown }).msg;
  return typeof nested === "string" ? nested : undefined;
};

/**
 * The HTTP status of a failed request, whatever shape it arrived in.
 *
 * Verified shapes, not assumed ones: `axiosBaseQuery` (util/axiosBaseQuery.ts)
 * normalises every API failure to `{ status, data }`, and that object is what
 * `.unwrap()` rejects with and what an `initiate()` result carries as `error`.
 * `status` is `err.response?.status`, so it is genuinely absent on a network
 * error, and anything that is not an axios error is re-emitted untouched (a
 * plain `Error`, which has no status either).
 */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
};

/**
 * Status -> message. `genericMessageKey` lets a payment flow say "the payment
 * could not be started" instead of "your booking could not be completed"; the
 * 403/409 answers are the same everywhere and are not overridable.
 */
export const bookingFailureMessageKey = (
  error: unknown,
  genericMessageKey: string = BOOKING_FAILURE_MESSAGE_KEYS.generic
): string => {
  // Read before the status switch: a booking-window refusal is a 400, which
  // would otherwise fall through to "please try again" — the one piece of
  // advice that cannot work here.
  const refusal = BOOKING_WINDOW_KEYS[bookingRefusalCodeOf(error) ?? ""];
  if (refusal) return refusal;

  switch (httpStatusOf(error)) {
    case 403:
      return BOOKING_FAILURE_MESSAGE_KEYS.forbidden;
    case 409:
      return BOOKING_FAILURE_MESSAGE_KEYS.conflict;
    default:
      return genericMessageKey;
  }
};

/** Tell the customer a booking / payment attempt did not go through. */
export const notifyBookingFailure = (
  dispatch: Dispatch,
  error: unknown,
  genericMessageKey?: string
): void => {
  dispatch(
    enqueueNotification({
      severity: "error",
      message: t(bookingFailureMessageKey(error, genericMessageKey)),
    })
  );
};

/**
 * Notify only when the status says something the customer can act on.
 *
 * The Stripe payment steps already render a persistent panel on a failed
 * initialisation (`paymentInitializationFailed` -> `InitializationErrorInfo`,
 * on all ten hosting screens), so a generic toast would repeat it word for
 * word. What that panel cannot know is *why*: it offers "please try again",
 * which is the wrong advice when the answer was 403 or 409 and the customer
 * has to pick something else. Returns whether a message was shown.
 *
 * `no_payment` is deliberately not in this group — every payment screen gates
 * the panel on `paymentProvider !== "no_payment"`, so a failed free booking
 * has no persistent message and needs the generic one.
 */
export const notifyActionableBookingFailure = (
  dispatch: Dispatch,
  error: unknown
): boolean => {
  const status = httpStatusOf(error);
  const isBookingWindowRefusal =
    BOOKING_WINDOW_KEYS[bookingRefusalCodeOf(error) ?? ""] !== undefined;

  // A booking-window refusal joins 403/409 for the same reason they are here:
  // the persistent panel's "please try again" is the wrong advice, and the
  // customer has to change something — here, the date.
  if (status !== 403 && status !== 409 && !isBookingWindowRefusal) {
    return false;
  }
  notifyBookingFailure(dispatch, error);
  return true;
};

/**
 * Tell the customer a list could not be loaded, so an empty screen is not
 * mistaken for "nothing available" (#1579/#1581). The message is per resource
 * because "we could not load this" is useless when three fetches feed one
 * screen.
 */
export const notifyLoadFailure = (
  dispatch: Dispatch,
  messageKey: string
): void => {
  dispatch(
    enqueueNotification({
      severity: "error",
      message: t(messageKey),
    })
  );
};

/**
 * The error of an RTK Query `initiate()` result, if it failed.
 *
 * `store.dispatch(endpoint.initiate(...))` **resolves** with `{ data }` or
 * `{ error }` — it does not reject. Every `fetch*Flow` wrapped that call in a
 * `try/catch` and read `data`, so an API failure never reached the `catch` at
 * all: the flow silently skipped its dispatch and the screen rendered an empty
 * list. Checking the result is what actually makes those failures detectable.
 */
export const queryResultError = (result: unknown): unknown | undefined => {
  if (!result || typeof result !== "object") {
    return undefined;
  }
  return (result as { error?: unknown }).error;
};
